import { useEffect, useState, useRef, useMemo } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { LoginPanel } from './components/LoginPanel';
import { Terminal } from './components/Terminal';
import { MultiTraderWorkspace } from './components/MultiTraderWorkspace';
import { LegalGate, hasAcceptedAll } from './components/LegalGate';
import { WelcomeHub } from './components/WelcomeHub';
import { initAnalytics } from './lib/analytics';
import type { RailView } from './components/LeftRail';
import { MultiTraderProvider } from './context/MultiTraderContext';
import { SharedPortfolioProvider } from './context/SharedPortfolioContext';
import { MockBrokerClient } from './api/mockBroker';
import { MockWsClient } from './api/mockWs';
import { BrokerClient } from './api/brokerClient';
import { WsClient } from './api/wsClient';
import { LandingPage } from './pages/LandingPage';
import { PageAnnouncement } from './components/PageAnnouncement';

const env = {
  userPoolId: (import.meta as any).env?.VITE_USER_POOL_ID ?? '',
  userPoolClientId: (import.meta as any).env?.VITE_USER_POOL_CLIENT_ID ?? '',
  demoMode: ((import.meta as any).env?.VITE_DEMO_MODE ?? '').toLowerCase() === 'true',
};

if (!env.demoMode) {
  console.error(
    '🚨 DEMO MODE NOT ENFORCED 🚨\n' +
    'VITE_DEMO_MODE is not set to "true". This application is in DEMO mode.\n' +
    'Real API calls are blocked. Set VITE_DEMO_MODE=true to enable this application.\n' +
    'If you are trying to enable real trading, you must explicitly enable it in the build config.'
  );
}

const FX_SESSION_KEY = 'nigastock.fx.session.v1';
const LEGAL_KEY = 'nigastock.legal.v2';
function markFxSessionActive(username?: string) {
  try {
    sessionStorage.setItem(FX_SESSION_KEY, JSON.stringify({
      authenticated: true,
      legals_ack: true,
      username: username ?? 'unknown',
      issued_at: Date.now(),
      expires_at: Date.now() + 8 * 60 * 60 * 1000,
    }));
  } catch { }
}
function clearFxSession() {
  try { sessionStorage.removeItem(FX_SESSION_KEY); } catch { }
}
function clearLegalState() {
  try { localStorage.removeItem(LEGAL_KEY); } catch { }
}

function Gated() {
  const auth = useAuth();
  const [legalOk, setLegalOk] = useState<boolean>(() => hasAcceptedAll());
  const [chosenProduct, setChosenProduct] = useState<RailView | undefined>();
  const path = window.location.pathname;

  const isDetachedWindow = new URLSearchParams(window.location.search).get('detached') === 'true';
  const hasAuthToken = localStorage.getItem('nigastock-auth-token') &&
                       localStorage.getItem('nigastock-auth-timestamp');
  const authTokenRecent = hasAuthToken &&
                          (Date.now() - parseInt(localStorage.getItem('nigastock-auth-timestamp') || '0')) < 3600000;

  useEffect(() => {}, []);

  useEffect(() => {
    if (auth.status === 'signed_in' && legalOk) {
      markFxSessionActive(auth.username);
      auth.getIdToken().then(token => {
        if (token) {
          localStorage.setItem('nigastock-auth-token', token);
          localStorage.setItem('nigastock-auth-timestamp', Date.now().toString());
        }
      }).catch(err => {
        console.warn('Failed to get auth token for detached windows:', err);
      });
    } else {
      clearFxSession();
      // NOTE: Do NOT clearLegalState() on logout. Legal acceptances are compliance records
      // that should persist across login sessions. Users should only re-accept if the
      // legal document version changes (which bumps the KEY in LegalGate.tsx).
      sessionStorage.removeItem('nigastock-auth-token');
    }
  }, [auth.status, auth.username, legalOk]);

  useEffect(() => {
    if (auth.status !== 'signed_in') {
      setChosenProduct(undefined);
      setLegalOk(false);
      // NOTE: Do NOT clearLegalState() here. Legal acceptances are persistent compliance
      // records. Reset only the UI state (legalOk) to force re-checking on next login,
      // but preserve the actual acceptance timestamps in localStorage.
    }
  }, [auth.status]);

  const detachedClients = useMemo(() => {
    if (!isDetachedWindow || !authTokenRecent) return null;

    const getIdTokenFromStorage = async () => {
      const token = localStorage.getItem('nigastock-auth-token');
      return token || '';
    };

    const broker = new BrokerClient({
      baseUrl: (import.meta as any).env?.VITE_API_BASE ?? '/api/v1',
      getIdToken: getIdTokenFromStorage,
    });
    const ws = new WsClient({
      endpoint: (import.meta as any).env?.VITE_WS_ENDPOINT ?? '',
      getIdToken: getIdTokenFromStorage,
    });

    return { broker, ws };
  }, [isDetachedWindow, authTokenRecent]);

  const demoClients = useMemo(() => {
    if (!env.demoMode) return null;
    const mockBroker = new MockBrokerClient();
    return {
      broker: mockBroker as any,
      ws: mockBroker.getWsClient() as any,
    };
  }, []);

  // DISABLED: This was preventing WelcomeHub from showing after login.
  // Users should always see WelcomeHub first to choose their trader.
  // Path-based auto-selection interfered with the intended login → WelcomeHub flow.
  // useEffect(() => {
  //   if (path === '/fx-trader' || path.startsWith('/fx-trader/')) {
  //     setChosenProduct('FX' as any);
  //   } else if (path === '/trader' || path.startsWith('/trader/')) {
  //     setChosenProduct('Trade' as any);
  //   }
  // }, [path]);

  const isTraderRoute = path === '/trader' || path.startsWith('/trader/');
  const isFxTraderRoute = path === '/fx-trader' || path.startsWith('/fx-trader/');

  const traderClients = useMemo(() => {
    const broker = demoClients?.broker ?? new BrokerClient({
      baseUrl: (import.meta as any).env?.VITE_API_BASE ?? '/api/v1',
      getIdToken: auth.getIdToken,
    });
    const ws = demoClients?.ws ?? new WsClient({
      endpoint: (import.meta as any).env?.VITE_WS_ENDPOINT ?? '',
      getIdToken: auth.getIdToken,
    });
    return { broker, ws };
  }, [auth.status, demoClients]);

  const terminalClients = useMemo(() => {
    const broker = demoClients?.broker ?? new BrokerClient({
      baseUrl: (import.meta as any).env?.VITE_API_BASE ?? '/api/v1',
      getIdToken: auth.getIdToken,
    });
    const ws = demoClients?.ws ?? new WsClient({
      endpoint: (import.meta as any).env?.VITE_WS_ENDPOINT ?? '',
      getIdToken: auth.getIdToken,
    });
    return { broker, ws };
  }, [auth.status, demoClients]);

  if (isDetachedWindow && authTokenRecent && detachedClients) {
    const { broker, ws } = detachedClients;
    return (
      <MultiTraderProvider>
        <SharedPortfolioProvider
          broker={broker}
          ws={ws}
          accountId={undefined}
          accountLabel="All Accounts"
          brokerId="default"
        >
          <MultiTraderWorkspace
            brokerClient={broker}
            wsClient={ws}
            onExit={() => setChosenProduct(undefined)}
          />
        </SharedPortfolioProvider>
      </MultiTraderProvider>
    );
  }

  if (auth.status === 'signed_in') {
    if (!legalOk) return <LegalGate onComplete={() => setLegalOk(true)} />;

    if (!chosenProduct) {
      return <WelcomeHub onChooseProduct={setChosenProduct} />;
    }

    if (chosenProduct === 'Dashboard' || chosenProduct === 'FX' || chosenProduct === 'Bond' || chosenProduct === 'ETF' || chosenProduct === 'Tools' || chosenProduct === 'Brokers' || chosenProduct === 'Funding') {
      return (
        <Terminal
          brokerOverride={terminalClients.broker}
          wsOverride={terminalClients.ws}
          initialView={chosenProduct as any}
          onExit={() => setChosenProduct(undefined)}
        />
      );
    }

    if (chosenProduct === 'Trade') {
      return (
        <MultiTraderProvider>
          <SharedPortfolioProvider
            broker={traderClients.broker}
            ws={traderClients.ws}
            accountId={undefined}
            accountLabel="All Accounts"
            brokerId="default"
          >
            <MultiTraderWorkspace
              brokerClient={traderClients.broker}
              wsClient={traderClients.ws}
              onExit={() => setChosenProduct(undefined)}
            />
          </SharedPortfolioProvider>
        </MultiTraderProvider>
      );
    }
  }

  return <LoginPanel />;
}

function Router() {
  const path = window.location.pathname;
  const isDetached = new URLSearchParams(window.location.search).get('detached') === 'true';
  const hasValidToken = localStorage.getItem('nigastock-auth-token') &&
                        localStorage.getItem('nigastock-auth-timestamp') &&
                        (Date.now() - parseInt(localStorage.getItem('nigastock-auth-timestamp') || '0')) < 3600000;

  if (path === '/' || path === '') {
    return <LandingPage />;
  }

  if (path === '/trader' || path.startsWith('/trader/') || path === '/fx-trader' || path.startsWith('/fx-trader/')) {
    return (
      <AuthProvider env={env}>
        <Gated />
      </AuthProvider>
    );
  }

  return <LandingPage />;
}

export default function App() {
  // Initialize analytics on app startup
  useEffect(() => {
    initAnalytics();
  }, []);

  if (!env.userPoolId || !env.userPoolClientId) {
    return (
      <div className="login-wrap">
        <div className="login">
          <h1>Configuration missing</h1>
          <div className="err">VITE_USER_POOL_ID and VITE_USER_POOL_CLIENT_ID must be set at build time.</div>
        </div>
      </div>
    );
  }
  return <Router />;
}
